import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Button from '@atlaskit/button/standard-button';
import MediaServicesAddCommentIcon from '@atlaskit/icon/glyph/media-services/add-comment';
import Tooltip from '@atlaskit/tooltip';

import { useIntl } from 'src/hooks/intl';
import {
  DIFFS_HAVE_RENDERED,
  OPEN_FILE_COMMENT,
  scrollToAnchor,
  toggleDiffComments,
} from 'src/redux/pull-request/actions';
import { getIsSingleFileModeActive } from 'src/redux/pull-request/selectors';
import { publishUiEvent } from 'src/utils/analytics/publish';
import { getFileHref } from 'src/utils/get-file-href';

import { useHiddenComments } from '../../hooks/diffs';

import messages from './file-level-add-comment-button.i18n';
import * as styles from './file-level-add-comment-button.style';

export type FileLevelAddCommentProps = {
  filePath: string;
};

const BaseFileLevelAddCommentButton = (props: FileLevelAddCommentProps) => {
  const { filePath } = props;
  const fileHref = getFileHref(filePath);

  const intl = useIntl();
  const areCommentsHidden = useHiddenComments(filePath);
  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);
  const dispatch = useDispatch();

  const handleAddComment = useCallback(() => {
    if (areCommentsHidden) {
      dispatch(toggleDiffComments(filePath));
    }
    dispatch({ type: OPEN_FILE_COMMENT, payload: filePath });
  }, [areCommentsHidden, filePath, dispatch]);

  const onClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleAddComment();
    dispatch(scrollToAnchor(fileHref));
    if (isSingleFileModeActive) {
      dispatch({ type: DIFFS_HAVE_RENDERED });
    }
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'fileLevelAddCommentButton',
      source: 'pullRequestScreen',
    });
  };

  return (
    <styles.FileLevelAddCommentButtonWrapper>
      <Tooltip
        content={intl.formatMessage(messages.addFileLevelCommentToolTip)}
      >
        <Button
          data-qa="pr-diff-file-comment"
          onClick={onClick}
          appearance="subtle"
          iconBefore={
            <MediaServicesAddCommentIcon
              label={intl.formatMessage(
                messages.addFileLevelCommentButtonLabel
              )}
            />
          }
        />
      </Tooltip>
    </styles.FileLevelAddCommentButtonWrapper>
  );
};

export const FileLevelAddCommentButton = React.memo(
  BaseFileLevelAddCommentButton
);
